import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EnvironmentInjector,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  ParamMap,
  Params,
  Router,
  RouterModule,
} from "@angular/router";
import { Modal, ModalService } from "@indice/ng-components";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  EMPTY,
  fromEvent,
} from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mergeMap,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { AuthService } from "src/app/core/services/auth.service";
import { GeocodeService } from "src/app/core/services/geocode.service";
import {
  PropertyListFilter,
  PropertySummary,
  PropertySummaryResultSet,
} from "src/app/services/portal-api.service";
import {
  PlaceViewModel,
  PropertyListFilterWithPagination,
  PropertyListingService,
} from "src/app/services/property.service";
import {
  Toast,
  ToasterService,
  ToastType,
} from "src/app/services/toaster.service";
import { PropertyCardItemComponent } from "src/app/shared/components/property-card-item/property-card-item.component";
import { ResponsiveService } from "src/app/shared/services/responsive.service";
import { MapComponent } from "../../../shared/components/map/map.component";
import { SortDropdownComponent } from "../../../shared/components/sort-dropdown/sort-dropdown.component";
import { PropertyModalComponent } from "../../property-modal/property-modal.component";
import { SaveSearchAnonymousModalComponent } from "../../save-search-modal/save-search-anonymous-modal/save-search-anonymous-modal.component";
import { SaveSearchModalComponent } from "../../save-search-modal/save-search-modal.component";
import { FilterDropdownItem } from "../filter-dropdown/filter-dropdown.component";
import { LoadingService } from "src/app/shared/services/loading.service";
import { LoaderComponent } from "src/app/shared/components/loader/loader.component";
import { localizeRoute } from "src/app/shared/utilities/localizeRoutes";
import { LanguageRouterLinkDirective } from "src/app/shared/directives/language-router-link.directive";
import { PaginationComponent } from "../../../shared/components/pagination/pagination.component";
import { FindPropertyService } from "src/app/core/services/find-property.service";

@Component({
  selector: "app-grid-list-cards",
  standalone: true,
  imports: [
    CommonModule,
    PropertyCardItemComponent,
    MapComponent,
    RouterModule,
    TranslocoDirective,
    SortDropdownComponent,
    LoaderComponent,
    LanguageRouterLinkDirective,
    PaginationComponent,
  ],
  templateUrl: "./grid-list-cards.component.html",
  styleUrl: "./grid-list-cards.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LoadingService],
})
export class GridListCardsComponent implements OnInit {
  @ViewChild("cardContainer") cardContainer!: ElementRef<HTMLDivElement>;
  // These are used by the pagination component
  public currentPage: any = 1;
  public pageSize = 10;
  public modelCount: number = 0;
  // Loading states
  public loading = false;
  public sectionLoading = false;
  public firstTimeLoading = true;
  public criteriaAdjusted: boolean = false;
  // Observable for all properties fetched from the server
  public allProperties$!: Observable<PropertySummaryResultSet>;
  // Observable for the currently displayed (paginated) properties
  public displayedProperties$!: Observable<any[]>;
  public queryParamSortString: string | undefined;
  public searchFilter: PropertyListFilterWithPagination | undefined;
  @Input() public displayMap: boolean = true;
  @Input() orderByItems: FilterDropdownItem[] = [];
  @Output() public onChangePropertyCount: EventEmitter<number> =
    new EventEmitter();
  toast$: Observable<Toast> = new Observable();
  activeAnchor: number = 0;
  mapSearch: boolean = true;
  isLoggedIn: boolean = false;
  isMobile = false;
  focusedCardId: string | undefined = undefined;
  // Lookup object to map keys to sort strings
  sortKeyMapping: { [key: string]: string | undefined } = {
    mostRecent: "publishedDate-",
    ascPrice: "price+",
    descPrice: "price-",
    ascSize: "propertyArea+",
    descSize: "propertyArea-",
    suggested: "suggested",
  };
  public selectedItem: FilterDropdownItem | undefined;
  public searchToastsTranslations: any;
  public readonly size: number = 5;
  protected pageByLoadMoreSubject = new BehaviorSubject(1);
  protected googleMapItems = new BehaviorSubject<PropertySummary[]>([]);
  // Subject to trigger a new page selection (pages are 1-indexed)
  private pageSubject = new BehaviorSubject<number>(1);

  private readonly acceptLanguage = "";
  private previousSearchParams: any = null;

  private debouncedMapItemsSubject = new Subject<PropertySummary[]>();
  private debouncedMapItems$ = this.debouncedMapItemsSubject
    .asObservable()
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300), // Adjust the time as necessary
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
      ), // Emit only if different
    );
  isButtonVisible = signal(true);

  private scrollTimeout: any;

  constructor(
    private propertyListingService: PropertyListingService,
    private readonly activatedRoute: ActivatedRoute,
    private _modalService: ModalService,
    @Inject(ToasterService) private _toastService: ToasterService,
    private geocodeService: GeocodeService,
    private router: Router,
    private transloco: TranslocoService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private responsiveService: ResponsiveService,
    private findPropertyService: FindPropertyService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector,
  ) {
    this.activatedRoute.queryParams
      .pipe(
        tap((t: Params) => {
          this.currentPage =
            Number(t["page"]) ?? (this.currentPage ? this.currentPage : 1);

          if (this.currentPage == 0 || isNaN(this.currentPage)) {
            this.currentPage = 1;
            this.updatePageParam({
              page: undefined,
            });
          }

          this.pageSubject.next(this.currentPage);
        }),
      )
      .subscribe();
    const scroll$ = fromEvent(window, "scroll");

    effect(() => {
      scroll$.subscribe(() => {
        this.isButtonVisible.set(false);

        // Debounce manually
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isButtonVisible.set(true);
        }, 1000);
      });
    });
  }

  public get canLoadMoreItems() {
    return this.currentPage * this.size < this.modelCount;
  }

  ngOnInit() {
    this.sectionLoading = true;
    this.transloco
      .selectTranslateObject(["searches.toasts", "find-property.grid-list"])
      .pipe(
        tap((translations) => {
          this.searchToastsTranslations = translations[0];
          this.applyTranslations(translations[1]);
        }),
      )
      .subscribe();

    this.allProperties$ = this.activatedRoute.queryParamMap.pipe(
      tap((params) => {
        if (this.paramsFilterChanged(this.previousSearchParams, params)) {
          this.loading = true;
        }

        this.debouncedMapItems$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((items) => {
            this.googleMapItems.next(items); // Emit updated items after debouncing
            this.cdr.detectChanges(); // Mark the component for check
          });
      }),
      switchMap((params) => {
        if (this.paramsFilterChanged(this.previousSearchParams, params)) {
          // Now the searchFilter has been updated by updateSortingFromParams.
          const sortKey = params.get("sort");
          const keyToUse = sortKey
            ? this.findKeyByValue(this.sortKeyMapping, sortKey)
            : "mostRecent";

          const selectedItem = this.orderByItems.find(
            (item) => item.key === keyToUse,
          );
          this.selectedItem = selectedItem;
          this.queryParamSortString = selectedItem
            ? this.sortKeyMapping[selectedItem.key] || ""
            : "";
          this.searchFilter =
            this.propertyListingService.getSearchFiltersFromUrl(params);

          this.previousSearchParams = params;

          return this.propertyListingService.getProperties(
            this.acceptLanguage,
            this.searchFilter.page,
            10, // Fetch all properties for now.
            this.queryParamSortString,
            undefined,
            this.searchFilter,
          );
        }

        return EMPTY;
      }),
      tap((res) => {
        this.criteriaAdjusted = res.criteriaAdjusted;
        this.modelCount = res.properties.count ?? 0;
        this.onChangePropertyCount.emit(this.modelCount);
        if (this.currentPage != 1) {
          this.resetPropertyList();
        }

        if (
          (this.cardContainer &&
            res.properties.items &&
            res.properties.items.length < 6) ||
          !res.properties
        ) {
          this.scrollToTarget("top-anchor");
        }

        if (
          this.currentPage <= 0 ||
          this.currentPage > Math.ceil(this.modelCount / this.pageSize)
        ) {
          this.currentPage = 1;
          this.updatePageParam({
            page: this.currentPage,
          });
        }

        this.cdr.detectChanges();
      }),
      map((res) => res.properties),
      shareReplay(1),
    );

    // Combine page selection with the fetched properties.
    this.displayedProperties$ = this.allProperties$.pipe(
      tap(() => {
        if (this.firstTimeLoading) {
          this.firstTimeLoading = false;
        }
        this.cdr.detectChanges();
      }),
      map((result) => {
        const properties = result.items || [];
        return properties;
      }),
      tap((_) => {
        this.loading = false;
        this.sectionLoading = false;
        this.cdr.detectChanges();
      }),
    );

    // The rest of your subscriptions…
    this.responsiveService.isMobile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
      });

    this.authService
      .isLoggedIn()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((x) => (this.isLoggedIn = x));
  }

  public propertyClickedInMap(property?: PropertySummary) {
    if (this.isMobile) {
      let propertyModal: Modal<PropertyModalComponent> =
        new Modal<PropertyModalComponent>();

      propertyModal = this._modalService.show(PropertyModalComponent, {
        keyboard: true,
        class: "h-dvh md:h-auto",
        initialState: { property },
      });
    } else {
      this.focusedCardId = property?.id;
      // this.scrollToTarget(property?.id as string);
    }
  }
  private applyTranslations(t: any) {
    this.orderByItems.forEach((item, index) => {
      if (t && t[item.key]) {
        item.label = t[item.key];
      }
    });
  }
  public SaveSearch() {
    let saveSearchModal: Modal<SaveSearchModalComponent> =
      new Modal<SaveSearchModalComponent>();
    saveSearchModal.onHidden?.subscribe((r: any) => {
      if (r?.result?.success == true) {
        this._toastService.showBanner(
          ToastType.Success,
          this.searchToastsTranslations["searchSavedTitle"],
          this.searchToastsTranslations["whereToFind"],
          3000,
        );
      }
    });

    if (!this.isLoggedIn) {
      this._modalService.show(SaveSearchAnonymousModalComponent, {
        keyboard: true,
        class: "h-dvh md:h-auto",
      });
    } else {
      saveSearchModal = this._modalService.show(SaveSearchModalComponent, {
        keyboard: true,
        class: "h-dvh md:h-auto",
        initialState: {
          SearchFilters: this.searchFilter,
        },
      });
    }
  }

  // Find the key for the given sortByKey
  findKeyByValue = (
    keyMapping: { [key: string]: string | undefined },
    value: string | undefined,
  ): string | undefined => {
    return Object.entries(keyMapping).find(([, v]) => v === value)?.[0];
  };

  paramsFilterChanged(previous: any, current: any) {
    if (!previous) return true;

    if (previous.params["page"] !== current.params["page"]) return true;

    if (
      Object.keys(previous.params).length !== Object.keys(current.params).length
    )
      return true;

    let result = false;
    const keys = Object.keys(previous.params).forEach((key) => {
      if (!result && previous.params[key] !== current.params[key]) {
        result = true;
      }
    });

    return result;
  }

  public mapViewChanged(bounds: google.maps.LatLngBounds) {
    if (this.mapSearch) {
      let place: PlaceViewModel = {
        Bounds: {
          SwLon: bounds.getSouthWest().lng() ?? undefined,
          NeLat: bounds.getNorthEast().lat() ?? undefined,
          SwLat: bounds.getSouthWest().lat() ?? undefined,
          NeLon: bounds.getNorthEast().lng() ?? undefined,
        },
      };
      let encodedMessage = this.geocodeService.protoEncodeGeoSearch(place);
      let urlencodedMessage = btoa(String.fromCharCode(...encodedMessage));
      let filter: PropertyListFilter = new PropertyListFilter({
        area: urlencodedMessage,
      });
      filter.searchTerm = undefined; // Clear search terms on map move
      this.findPropertyService.setMapMustDebounce(false);
      this.router.navigate([localizeRoute("/properties", this.envInjector)], {
        queryParams: { ...filter, searchFromMap: true, page: 1 },
        queryParamsHandling: "merge",
      });
    }
  }

  /**
   * Scrolls a property card into view and attempts to center it in the
   * scrollable container, respecting sticky headers or offsets.
   *
   * @param {string} id - The ID of the property card element to scroll into view.
   *
   * The method checks if the property card is near the container's top or bottom.
   * If it is, it will use `scrollIntoView` with `block: 'nearest'` to ensure the
   * card is fully visible. Otherwise, it calculates an offset to attempt to
   * center the card vertically. It also accounts for a sticky header via
   * `stickyOffset`.
   */
  scrollToTarget(id: string) {
    // Try to get the element by id from the current view.
    let propertyElement = document.getElementById(id);

    // If the element exists, scroll it into view.
    if (propertyElement) {
      const containerEl = this.cardContainer?.nativeElement;
      if (!containerEl) {
        return;
      }
      const containerRect = containerEl.getBoundingClientRect();
      const cardRect = propertyElement.getBoundingClientRect();
      const stickyOffset = 390;
      // (Optional) Compute an offset if you want to adjust scrolling.
      const offsetTop =
        cardRect.top - containerRect.top + containerEl.scrollTop - stickyOffset;

      // Determine if it's near the container edges.
      const isLastElement =
        propertyElement.nextElementSibling === null ||
        propertyElement.nextElementSibling?.getBoundingClientRect().top >
          containerRect.bottom;

      propertyElement.scrollIntoView({
        behavior: "smooth",
        block: isLastElement ? "end" : "center",
      });
    } else {
      this.allProperties$
        .pipe(take(1))
        .subscribe((result: PropertySummaryResultSet) => {
          const allItems = result.items || [];
          // Find the overall index of the property.
          const index = allItems.findIndex((prop) => prop.id === id);
          if (index === -1) {
            return; // Property not found.
          }
          // Compute page number (assuming pages are 1-indexed)
          const pageNumber = Math.floor(index / this.pageSize) + 1;
          // If it's not the current page, update pagination.
          if (pageNumber !== this.currentPage) {
            this.onPageSelect(pageNumber);
            // After a short delay (to let Angular update the view),
            // try scrolling again.
            setTimeout(() => {
              const el = document.getElementById(id);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 300); // Adjust delay as needed.
          }
        });
    }
  }

  trackByPropertyId(index: number, property: PropertySummary): string {
    return property.id!; // or whatever unique identifier your properties have
  }

  /**
   * Called when the pagination component emits a page selection.
   */
  onPageSelect(page: number, scrollToTop?: boolean | undefined) {
    const lastPage = Math.ceil(this.modelCount / this.pageSize);
    // Ensure the page number is valid
    if (page < 1 || page > lastPage) {
      return;
    }
    this.currentPage = page;
    this.pageSubject.next(page);

    if (scrollToTop) {
      const pageIsFull = page * this.pageSize === this.modelCount;
      if (page !== lastPage || pageIsFull) {
        this.cardContainer.nativeElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        // Called only for the last page, if it contains less results than this.pageSize
        this.scrollToTarget("top-anchor");
      }
    }

    this.updatePageParam({
      page: page,
    });

    this.resetPropertyList();
  }

  getSortKeyMapping(sortByKey: string | undefined = "suggested") {
    this.selectedItem = this.orderByItems?.find(
      (item) => item.key === sortByKey,
    );

    if (this.selectedItem) {
      this.queryParamSortString =
        this.sortKeyMapping?.[this.selectedItem.key] || ""; // Default to empty if key not found
    }
  }

  orderByChanged(sortByKey: string | undefined) {
    this.getSortKeyMapping(sortByKey);

    this.router.navigate([localizeRoute("/properties", this.envInjector)], {
      queryParams: {
        sort:
          sortByKey && this.queryParamSortString
            ? this.queryParamSortString
            : undefined,
      },
      queryParamsHandling: "merge",
    });
  }

  toggleDisplay() {
    this.displayMap = !this.displayMap;
    window.scrollTo({ behavior: "smooth", top: 0 });
    this.findPropertyService.setMapMustDebounce(true);
  }

  private updatePageParam(page: Partial<IPageNumber>): void {
    const currentParams = this.activatedRoute.snapshot.queryParams;
    const updatedParams = { ...currentParams, ...page };

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: updatedParams,
      queryParamsHandling: "merge",
      replaceUrl: false, // replaces history state instead of pushing a new one
    });
  }

  protected changePage(page: number) {
    if (this.loading) return;
    if (!this.canLoadMoreItems) return;
    this.currentPage = page;
    this.pageByLoadMoreSubject.next(this.currentPage);
  }

  private resetPropertyList() {
    //this.currentPage = 1;
    this.pageSubject.next(1);
  }
}

export interface IPageNumber {
  page: number;
}
